import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../../components/layout/Layout';
import ChevronRightIcon from '../../assets/icons/chevron-right.svg';
import { 
  ArchiveCardSkeleton, 
  CommunityCardSkeleton 
} from '../../components/common/SkeletonLoaders';
import { 
  searchAll, 
  SearchResults as ApiSearchResults,
  LectureSearchItem,
  LecturerSearchItem,
  PostSearchItem,
  ArchiveSearchItem
} from '../../services/searchApi';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { pickThumbnail } from '../../utils/thumbnail';

// 탭 타입 정의
type TabType = 'all' | 'class' | 'coffee-chat' | 'archive' | 'community';

// 변환된 검색 결과 데이터 타입 정의
interface SearchResultItem {
  id: number;
  title: string;
  caption?: string;
  content?: string;
  thumbnailUrl?: string;
  author?: string;
  date?: string;
  views?: number;
  replies?: number;
  category?: string;
  parentCategory?: string;
  tags?: string[];
  extraCaption?: string;
}

interface SearchResults {
  class: SearchResultItem[];
  coffeeChat: SearchResultItem[];
  archive: SearchResultItem[];
  community: SearchResultItem[];
}

const SearchIndex: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [inputValue, setInputValue] = useState(searchParams.get('q') || ''); // 입력 필드 값
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || ''); // 실제 검색어
  const [searchResults, setSearchResults] = useState<SearchResults>({
    class: [],
    coffeeChat: [],
    archive: [],
    community: []
  });
  const [isLoading, setIsLoading] = useState(false);

  // 무한 스크롤을 위한 상태 - 클래스 탭용
  const [classPage, setClassPage] = useState(0);
  const [hasMoreClass, setHasMoreClass] = useState(false);
  const [isLoadingMoreClass, setIsLoadingMoreClass] = useState(false);
  const [displayedClassItems, setDisplayedClassItems] = useState<SearchResultItem[]>([]);
  const ITEMS_PER_PAGE = 12; // 한 번에 로드할 아이템 수

  // 이미지 로딩 상태 관리
  const [imageLoadStates, setImageLoadStates] = useState<{[key: string]: boolean}>({});

  // API 응답을 UI 형태로 변환하는 함수들
  const convertLectureToSearchItem = useCallback((lecture: LectureSearchItem): SearchResultItem => ({
    id: lecture.lecture_info.id,
    title: lecture.lecture_info.title,
    thumbnailUrl: pickThumbnail(lecture.lecture_info),
    tags: lecture.lecture_info.tags,
    extraCaption: `${lecture.lecturer_info.name}${lecture.lecturer_info.department ? ` · ${lecture.lecturer_info.department}` : ''}`
  }), []);

  const convertLecturerToSearchItem = useCallback((lecturer: LecturerSearchItem): SearchResultItem => ({
    id: lecturer.id,
    title: lecturer.name,
    caption: lecturer.department || undefined,
    thumbnailUrl: lecturer.image_url,
    tags: lecturer.tags || [],
    extraCaption: lecturer.department || undefined
  }), []);

  const convertArchiveToSearchItem = useCallback((archive: ArchiveSearchItem): SearchResultItem => ({
    id: archive.id,
    title: archive.title,
    content: archive.htmlcontent.replace(/<[^>]*>/g, ''), // HTML 태그 제거
    author: archive.writerName,
    date: new Date(archive.postDate).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }),
    views: archive.viewCnt
  }), []);

  const convertPostToSearchItem = useCallback((post: PostSearchItem): SearchResultItem => ({
    id: post.id,
    title: post.title,
    content: post.htmlcontent.replace(/<[^>]*>/g, ''), // HTML 태그 제거
    parentCategory: post.category,
    category: post.studyCategoryName || '일반',
    author: post.writerName,
    date: new Date(post.postDate).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }),
    replies: 0, // API에서 제공되지 않아 기본값
    views: 0 // API에서 제공되지 않아 기본값
  }), []);

  // 이미지 로드 핸들러
  const handleImageLoad = (imageKey: string) => {
    setImageLoadStates(prev => ({
      ...prev,
      [imageKey]: true
    }));
  };

  const handleImageErrorDefault = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.src = '/images/no-image-dummy.png';
  };

  const handleAvatarError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.src = '/images/default-avatar.png';
  };

  const performSearch = useCallback(async (query: string) => {
    if (!query.trim()) return;
    
    setIsLoading(true);
    try {
      const apiResults: ApiSearchResults = await searchAll(query);
      
      // API 응답을 UI 형태로 변환
      const convertedResults: SearchResults = {
        class: apiResults.lectures.map(convertLectureToSearchItem),
        coffeeChat: apiResults.lecturers.map(convertLecturerToSearchItem),
        archive: apiResults.archives.map(convertArchiveToSearchItem),
        community: apiResults.posts.map(convertPostToSearchItem)
      };
      
      setSearchResults(convertedResults);
      
      // 클래스 탭 무한 스크롤 초기화
      setClassPage(0);
      setDisplayedClassItems(convertedResults.class.slice(0, ITEMS_PER_PAGE));
      setHasMoreClass(convertedResults.class.length > ITEMS_PER_PAGE);
    } catch (error) {
      /// console.error('Search failed:', error);
      // 에러 발생 시 빈 결과로 설정
      setSearchResults({
        class: [],
        coffeeChat: [],
        archive: [],
        community: []
      });
      setDisplayedClassItems([]);
      setHasMoreClass(false);
    } finally {
      setIsLoading(false);
    }
  }, [convertLectureToSearchItem, convertLecturerToSearchItem, convertArchiveToSearchItem, convertPostToSearchItem, ITEMS_PER_PAGE]);

  useEffect(() => {
    const query = searchParams.get('q');
    if (query) {
      setSearchQuery(query);
      setInputValue(query);
      performSearch(query);
    }
  }, [searchParams, performSearch]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setInputValue(query);
    setSearchParams({ q: query });
    performSearch(query);
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    
    // 클래스 탭으로 전환 시 초기 데이터 표시
    if (tab === 'class' && searchResults.class.length > 0) {
      setClassPage(0);
      setDisplayedClassItems(searchResults.class.slice(0, ITEMS_PER_PAGE));
      setHasMoreClass(searchResults.class.length > ITEMS_PER_PAGE);
    }
  };

  // 클래스 더보기 로드 함수
  const loadMoreClass = useCallback(() => {
    if (!hasMoreClass || isLoadingMoreClass) return;

    setIsLoadingMoreClass(true);
    const nextPage = classPage + 1;
    const startIndex = nextPage * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const newItems = searchResults.class.slice(startIndex, endIndex);
    
    // 실제 로딩 효과를 위한 딜레이
    setTimeout(() => {
      setDisplayedClassItems(prev => [...prev, ...newItems]);
      setClassPage(nextPage);
      setHasMoreClass(endIndex < searchResults.class.length);
      setIsLoadingMoreClass(false);
    }, 300);
  }, [classPage, hasMoreClass, isLoadingMoreClass, searchResults.class, ITEMS_PER_PAGE]);

  // 무한 스크롤 훅 사용
  useInfiniteScroll({
    hasMore: hasMoreClass && activeTab === 'class',
    isLoading: isLoadingMoreClass,
    onLoadMore: loadMoreClass,
    threshold: 200,
  });

  const handleClearInput = () => {
    setInputValue(''); // 입력 필드만 비우기
    /// console.log('After clear - searchQuery:', searchQuery, 'hasResults:', hasResults()); // 디버깅
    // searchQuery와 searchResults는 유지하여 검색 결과를 보여준 상태로 유지
  };

  const getTabCounts = () => {
    return {
      class: searchResults.class.length,
      coffeeChat: searchResults.coffeeChat.length,
      archive: searchResults.archive.length,
      community: searchResults.community.length
    };
  };

  const hasResults = () => {
    const counts = getTabCounts();
    return counts.class > 0 || counts.coffeeChat > 0 || counts.archive > 0 || counts.community > 0;
  };

  return (
    <Layout>
      <Container>
        {/* 검색 헤더 */}
        <SearchHeader>
          <SearchInputContainer>
            <SearchIcon />
            <SearchInput
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch(inputValue);
                }
              }}
              placeholder="검색어를 입력해주세요."
            />
            {inputValue && (
              <ClearButton onClick={handleClearInput}>
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" clipRule="evenodd" d="M1.92573 11.0003C1.92573 5.98831 5.98873 1.92529 11.0007 1.92529C16.0127 1.92529 20.0757 5.98831 20.0757 11.0003C20.0757 16.0123 16.0127 20.0753 11.0007 20.0753C5.98873 20.0753 1.92573 16.0123 1.92573 11.0003ZM8.37572 7.20862C8.05354 6.88644 7.53118 6.88644 7.209 7.20862C6.88682 7.5308 6.88682 8.05316 7.209 8.37535L9.83396 11.0003L7.209 13.6253C6.88682 13.9475 6.88682 14.4698 7.209 14.792C7.53118 15.1142 8.05354 15.1142 8.37572 14.792L11.0007 12.167L13.6256 14.792C13.9478 15.1142 14.4702 15.1142 14.7924 14.792C15.1145 14.4698 15.1145 13.9475 14.7924 13.6253L12.1674 11.0003L14.7924 8.37535C15.1145 8.05316 15.1145 7.5308 14.7924 7.20862C14.4702 6.88644 13.9478 6.88644 13.6256 7.20862L11.0007 9.83359L8.37572 7.20862Z" fill="#37383C" fillOpacity="0.28"/>
                </svg>
              </ClearButton>
            )}
          </SearchInputContainer>
        </SearchHeader>

        {/* 검색 결과 제목 */}
        <SearchResultTitle>'{searchQuery || ''}' 검색 결과</SearchResultTitle>

        {/* 탭 네비게이션 */}
        <TabContainer>
          <TabList>
            <Tab 
              active={activeTab === 'all'} 
              onClick={() => handleTabChange('all')}
            >
              전체
            </Tab>
            <Tab 
              active={activeTab === 'class'} 
              onClick={() => handleTabChange('class')}
            >
              클래스
            </Tab>
            <Tab 
              active={activeTab === 'coffee-chat'} 
              onClick={() => handleTabChange('coffee-chat')}
            >
              커피챗
            </Tab>
            <Tab 
              active={activeTab === 'archive'} 
              onClick={() => handleTabChange('archive')}
            >
              아카이브
            </Tab>
            <Tab 
              active={activeTab === 'community'} 
              onClick={() => handleTabChange('community')}
            >
              커뮤니티
            </Tab>
          </TabList>
        </TabContainer>

        {/* 검색 결과 컨텐츠 */}
        {isLoading ? (
          <ResultsContainer>
            {/* 로딩 중일 때 스켈레톤 UI 표시 */}
            {(activeTab === 'all' || activeTab === 'class') && (
              <Section>
                <SectionHeader>
                  <SectionTitle>클래스</SectionTitle>
                </SectionHeader>
                <ClassGrid>
                  {Array.from({ length: activeTab === 'class' ? 12 : 4 }).map((_, index) => (
                    <ClassCard key={`class-skeleton-${index}`}>
                      <ClassThumbnail>
                        <ClassImageSkeleton />
                      </ClassThumbnail>
                      <ClassInfo>
                        <TagSkeleton />
                        <ClassTitleSkeleton />
                        <ClassExtraCaptionSkeleton />
                      </ClassInfo>
                    </ClassCard>
                  ))}
                </ClassGrid>
              </Section>
            )}

            {(activeTab === 'all' || activeTab === 'coffee-chat') && (
              <Section>
                <SectionHeader>
                  <SectionTitle>커피챗</SectionTitle>
                </SectionHeader>
                <ClassGrid>
                  {Array.from({ length: activeTab === 'coffee-chat' ? 12 : 4 }).map((_, index) => (
                    <ClassCard key={`coffee-skeleton-${index}`}>
                      <ClassThumbnail>
                        <ClassImageSkeleton />
                      </ClassThumbnail>
                      <ClassInfo>
                        <TagSkeleton />
                        <ClassTitleSkeleton />
                        <ClassExtraCaptionSkeleton />
                      </ClassInfo>
                    </ClassCard>
                  ))}
                </ClassGrid>
              </Section>
            )}

            {(activeTab === 'all' || activeTab === 'archive') && (
              <Section>
                <SectionHeader>
                  <SectionTitle>아카이브</SectionTitle>
                </SectionHeader>
                <CommunityList>
                  {Array.from({ length: 2 }).map((_, index) => (
                    <ArchiveCardSkeleton key={`archive-skeleton-${index}`} />
                  ))}
                </CommunityList>
              </Section>
            )}

            {(activeTab === 'all' || activeTab === 'community') && (
              <Section>
                <SectionHeader>
                  <SectionTitle>커뮤니티</SectionTitle>
                </SectionHeader>
                <CommunityList>
                  {Array.from({ length: 2 }).map((_, index) => (
                    <CommunityCardSkeleton key={`community-skeleton-${index}`} />
                  ))}
                </CommunityList>
              </Section>
            )}
          </ResultsContainer>
        ) : hasResults() ? (
          <ResultsContainer>
            {/* 클래스 섹션 - 결과가 있을 때만 표시 */}
            {(activeTab === 'all' || activeTab === 'class') && searchResults.class.length > 0 && (
              <Section>
                <SectionHeader>
                  <SectionTitle>클래스</SectionTitle>
                  <SectionCount>{searchResults.class.length}</SectionCount>
                </SectionHeader>
                <ClassGrid>
                  {(activeTab === 'class' ? displayedClassItems : searchResults.class.slice(0, 4)).map((item) => (
                    <ClassCard key={item.id} onClick={() => navigate(`/class/lecture/${item.id}`)}>
                      <ClassThumbnail>
                        {!imageLoadStates[`class-${item.id}`] && (
                          <ClassImageSkeleton />
                        )}
                        <ClassImage 
                          src={item.thumbnailUrl} 
                          alt={item.title}
                          style={{ 
                            display: imageLoadStates[`class-${item.id}`] ? 'block' : 'none' 
                          }}
                          onLoad={() => handleImageLoad(`class-${item.id}`)}
                          onError={handleImageErrorDefault}
                        />
                      </ClassThumbnail>
                      <ClassInfo>
                        {/* 태그 표시 */}
                        {item.tags && item.tags.length > 0 && (
                          <TagContainer>
                            {item.tags.slice(0, 4).map((tag, index) => (
                              <Tag key={index}>{tag}</Tag>
                            ))}
                          </TagContainer>
                        )}
                        <ClassTitle>{item.title}</ClassTitle>
                        <ClassExtraCaption>{item.extraCaption}</ClassExtraCaption>
                      </ClassInfo>
                    </ClassCard>
                  ))}
                </ClassGrid>
                {activeTab === 'all' && searchResults.class.length > 4 && (
                  <ViewAllButton onClick={() => handleTabChange('class')}>
                    <span>클래스 전체보기</span>
                    <ChevronIcon>
                      <img src={ChevronRightIcon} alt="더보기" />
                    </ChevronIcon>
                  </ViewAllButton>
                )}
                
                {/* 클래스 탭에서 무한 스크롤 트리거 */}
                {activeTab === 'class' && hasMoreClass && (
                  <>
                    {isLoadingMoreClass && (
                      <LoadingMoreContainer>
                        <LoadingSpinner />
                        <LoadingText>더 많은 클래스를 불러오는 중...</LoadingText>
                      </LoadingMoreContainer>
                    )}
                  </>
                )}
              </Section>
            )}

            {/* 커피챗 섹션 - 결과가 있을 때만 표시 */}
            {(activeTab === 'all' || activeTab === 'coffee-chat') && searchResults.coffeeChat.length > 0 && (
              <Section>
                <SectionHeader>
                  <SectionTitle>커피챗</SectionTitle>
                  <SectionCount>{searchResults.coffeeChat.length}</SectionCount>
                </SectionHeader>
                <ClassGrid>
                  {(activeTab === 'coffee-chat' ? searchResults.coffeeChat : searchResults.coffeeChat.slice(0, 4)).map((item) => (
                    <ClassCard key={item.id} onClick={() => navigate(`/coffee-chat/${item.id}`)}>
                      <ClassThumbnail>
                        {!imageLoadStates[`coffee-${item.id}`] && (
                          <ClassImageSkeleton />
                        )}
                        <ClassImage 
                          src={item.thumbnailUrl} 
                          alt={item.title}
                          style={{ 
                            display: imageLoadStates[`coffee-${item.id}`] ? 'block' : 'none' 
                          }}
                          onLoad={() => handleImageLoad(`coffee-${item.id}`)}
                          onError={handleImageErrorDefault}
                        />
                      </ClassThumbnail>
                      <ClassInfo>
                        {/* 태그 표시 */}
                        {item.tags && item.tags.length > 0 && (
                          <TagContainer>
                            {item.tags.slice(0, 4).map((tag, index) => (
                              <Tag key={index}>{tag}</Tag>
                            ))}
                          </TagContainer>
                        )}
                        <ClassTitle>{item.title}</ClassTitle>
                        <ClassExtraCaption>{item.extraCaption}</ClassExtraCaption>
                      </ClassInfo>
                    </ClassCard>
                  ))}
                </ClassGrid>
                {activeTab === 'all' && searchResults.coffeeChat.length > 4 && (
                  <ViewAllButton onClick={() => handleTabChange('coffee-chat')}>
                    <span>커피챗 전체보기</span>
                    <ChevronIcon>
                      <img src={ChevronRightIcon} alt="더보기" />
                    </ChevronIcon>
                  </ViewAllButton>
                )}
              </Section>
            )}

            {/* 아카이브 섹션 - 결과가 있을 때만 표시 */}
            {(activeTab === 'all' || activeTab === 'archive') && searchResults.archive.length > 0 && (
              <Section>
                <SectionHeader>
                  <SectionTitle>아카이브</SectionTitle>
                  <SectionCount>{searchResults.archive.length}</SectionCount>
                </SectionHeader>
                <CommunityList>
                  {(activeTab === 'archive' ? searchResults.archive : searchResults.archive.slice(0, 2)).map((item) => (
                    <CommunityCard key={item.id} onClick={() => navigate(`/archive/${item.id}`)}>
                      <CommunityContent>
                        <CommunityMain>
                          <CommunityTitle>{item.title}</CommunityTitle>
                          <CommunityBody>{item.content}</CommunityBody>
                        </CommunityMain>
                        <CommunityMeta>
                          <CommunityAuthor>
                            <ArchiveAvatar 
                              src="/images/default-avatar.png" 
                              alt="avatar"
                              onError={handleAvatarError}
                            />
                            <span>{item.author}</span>
                            <Divider />
                            <span>{item.date}</span>
                          </CommunityAuthor>
                          <CommunityStats>
                            <span>조회수 {item.views}</span>
                          </CommunityStats>
                        </CommunityMeta>
                      </CommunityContent>
                    </CommunityCard>
                  ))}
                </CommunityList>
                {activeTab === 'all' && searchResults.archive.length > 2 && (
                  <ViewAllButton onClick={() => handleTabChange('archive')}>
                    <span>아카이브 전체보기</span>
                    <ChevronIcon>
                      <img src={ChevronRightIcon} alt="더보기" />
                    </ChevronIcon>
                  </ViewAllButton>
                )}
              </Section>
            )}

            {/* 커뮤니티 섹션 - 결과가 있을 때만 표시 */}
            {(activeTab === 'all' || activeTab === 'community') && searchResults.community.length > 0 && (
              <Section>
                <SectionHeader>
                  <SectionTitle>커뮤니티</SectionTitle>
                  <SectionCount>{searchResults.community.length}</SectionCount>
                </SectionHeader>
                <CommunityList>
                  {(activeTab === 'community' ? searchResults.community : searchResults.community.slice(0, 2)).map((item) => (
                    <CommunityCard key={item.id} onClick={() => { /// console.log("item@#@#", item); 
                    navigate(`/community/post/${item.id}`)}}>
                      <CommunityContent>
                        <CommunityMain>
                          <CommunityTitle>{item.title}</CommunityTitle>
                          <CommunityBody>{item.content}</CommunityBody>
                          <CommunityCategories>
                            <span>{item.parentCategory}</span>
                            <Divider />
                            <span>{item.category}</span>
                          </CommunityCategories>
                        </CommunityMain>
                        <CommunityMeta>
                          <CommunityAuthor>
                            <ArchiveAvatar 
                              src="/images/default-avatar.png" 
                              alt="avatar"
                              onError={handleAvatarError}
                            />
                            <span>{item.author}</span>
                            <Divider />
                            <span>{item.date}</span>
                          </CommunityAuthor>
                          <CommunityStats>
                            <span>답변 {item.replies}</span>
                            <Divider />
                            <span>조회수 {item.views}</span>
                          </CommunityStats>
                        </CommunityMeta>
                      </CommunityContent>
                    </CommunityCard>
                  ))}
                </CommunityList>
                {activeTab === 'all' && searchResults.community.length > 0 && (
                  <ViewAllButton onClick={() => handleTabChange('community')}>
                    <span>커뮤니티 전체보기</span>
                    <ChevronIcon>
                      <img src={ChevronRightIcon} alt="더보기" />
                    </ChevronIcon>
                  </ViewAllButton>
                )}
              </Section>
            )}
            
            {/* 특정 탭에서 해당 카테고리에 결과가 없을 때 빈 상태 메시지 */}
            {activeTab === 'class' && searchResults.class.length === 0 && (
              <EmptyState>
                <EmptyStateTitle>'{searchQuery}'에 대한 검색 결과가 없습니다.</EmptyStateTitle>
                <EmptyStateDescription>다른 키워드로 검색해보시거나 전체 클래스를 확인해보세요.</EmptyStateDescription>
              </EmptyState>
            )}
            
            {activeTab === 'coffee-chat' && searchResults.coffeeChat.length === 0 && (
              <EmptyState>
                <EmptyStateTitle>'{searchQuery}'에 대한 검색 결과가 없습니다.</EmptyStateTitle>
                <EmptyStateDescription>다른 키워드로 검색해보시거나 전체 커피챗을 확인해보세요.</EmptyStateDescription>
              </EmptyState>
            )}
            
            {activeTab === 'archive' && searchResults.archive.length === 0 && (
              <EmptyState>
                <EmptyStateTitle>'{searchQuery}'에 대한 검색 결과가 없습니다.</EmptyStateTitle>
                <EmptyStateDescription>다른 키워드로 검색해보시거나 전체 아카이브를 확인해보세요.</EmptyStateDescription>
              </EmptyState>
            )}
            
            {activeTab === 'community' && searchResults.community.length === 0 && (
              <EmptyState>
                <EmptyStateTitle>'{searchQuery}'에 대한 검색 결과가 없습니다.</EmptyStateTitle>
                <EmptyStateDescription>다른 키워드로 검색해보시거나 전체 커뮤니티를 확인해보세요.</EmptyStateDescription>
              </EmptyState>
            )}
          </ResultsContainer>
        ) : searchQuery ? (
          <NoResultsContainer>
            <NoResultsTitle>'{searchQuery}'에 대한 검색 결과가 없습니다.</NoResultsTitle>
            <NoResultsSubtitle>다른 검색어로 다시 검색해 보세요.</NoResultsSubtitle>
          </NoResultsContainer>
        ) : null}
      </Container>
    </Layout>
  );
};

export default SearchIndex;
// Styled Components
const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  padding: 64px 40px 128px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 48px;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 64px 20px 128px;
  }
`;

const SearchHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: 1400px;
`;

const SearchInputContainer = styled.div`
  width: 100%;
  max-width: 588px;
  padding: 12px;
  background: rgba(112, 115, 124, 0.08);
  border-radius: 12px;
  backdrop-filter: blur(32px);
  display: flex;
  align-items: center;
  gap: 8px;
  box-sizing: border-box;
`;

const SearchIcon = styled.div`
  width: 20px;
  height: 20px;
  padding: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
  
  &::after {
    content: '';
    width: 16px;
    height: 16px;
    background: var(--Label-Normal, #171719);
    mask: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M8.33331 1.75C4.69745 1.75 1.75 4.69746 1.75 8.33333C1.75 11.9692 4.69745 14.9167 8.33331 14.9167C9.88105 14.9167 11.3041 14.3826 12.428 13.4886L16.5531 17.6136C16.846 17.9065 17.3208 17.9065 17.6137 17.6136C17.9066 17.3207 17.9066 16.8459 17.6137 16.553L13.4887 12.4279C14.3826 11.3039 14.9166 9.88101 14.9166 8.33333C14.9166 4.69746 11.9692 1.75 8.33331 1.75ZM3.24999 8.33333C3.24999 5.52589 5.52587 3.25 8.33331 3.25C11.1407 3.25 13.4166 5.52589 13.4166 8.33333C13.4166 11.1408 11.1407 13.4167 8.33331 13.4167C5.52587 13.4167 3.24999 11.1408 3.24999 8.33333Z' fill='%23171719'/%3E%3C/svg%3E") no-repeat center;
    mask-size: contain;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  color: var(--Label-Normal, #171719);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  outline: none;

  &::placeholder {
    color: var(--Label-Normal, #171719);
  }
`;

const ClearButton = styled.button`
  width: 22px;
  height: 22px;
  padding: 1px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  
  svg {
    width: 22px;
    height: 22px;
  }
`;

const SearchResultTitle = styled.h1`
  color: var(--Label-Normal, #171719);
  font-size: 40px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 52px;
  text-align: center;
  width: 100%;
  max-width: 1400px;
  
  @media (max-width: 768px) {
    font-size: 32px;
    line-height: 42px;
  }
  
  @media (max-width: 480px) {
    font-size: 28px;
    line-height: 36px;
  }
`;

const TabContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const TabList = styled.div`
  width: 100%;
  height: 48px;
  background: var(--Background-Normal-Normal, white);
  border-bottom: 1px solid var(--Line-Normal-Alternative, rgba(112, 115, 124, 0.08));
  display: flex;
  align-items: center;
  gap: 32px;
`;

const Tab = styled.button<{ active: boolean }>`
  height: 48px;
  padding: 12px 0;
  border: none;
  background: transparent;
  color: ${props => props.active ? 'var(--Primary-Strong, #296768)' : 'var(--Label-Assistive, rgba(55, 56, 60, 0.28))'};
  font-size: 17px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;
  position: relative;

  ${props => props.active && `
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 2px;
      background: var(--Primary-Strong, #296768);
    }
  `}
`;

const NoResultsContainer = styled.div`
  padding-top: 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

const NoResultsTitle = styled.div`
  color: var(--Label-Strong, black);
  font-size: 22px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 30.01px;
`;

const NoResultsSubtitle = styled.div`
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
`;

const ResultsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 48px;
`;

const Section = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
`;

const SectionHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const SectionTitle = styled.h2`
  color: var(--Label-Strong, black);
  font-size: 22px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 30.01px;
`;

const SectionCount = styled.span`
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 22px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 30.01px;
`;

// 새로운 카드 스타일 - ClassIndex 기반
const ClassGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 24px;
  
  @media (max-width: 1400px) {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const ClassCard = styled.div`
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 12px;
  display: inline-flex;
  cursor: pointer;
  border-radius: 8px;
  padding: 8px;
  margin: -8px;
`;

const ClassThumbnail = styled.div`
  align-self: stretch;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  display: flex;
  border: 1px solid rgba(112, 115, 124, 0.08);
  aspect-ratio: 3/2;
  min-height: 188px; /* 최소 높이 보장 */
`;

const ClassImage = styled.img`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  object-fit: cover;
  user-select: none;
  pointer-events: none;
  -webkit-user-drag: none;
  -webkit-user-select: none;
  transition: transform 0.3s ease;
  
  ${ClassCard}:hover & {
    transform: scale(1.05);
  }
`;

const ClassInfo = styled.div`
  align-self: stretch;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 4px;
  display: flex;
`;

const ClassTitle = styled.div`
  align-self: stretch;
  color: var(--Label-Normal, #171719);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  word-wrap: break-word;
`;

const ClassExtraCaption = styled.div`
  align-self: stretch;
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 13px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.25px;
  word-wrap: break-word;
`;

// 태그 컨테이너와 태그 스타일
const TagContainer = styled.div`
  align-self: stretch;
  overflow: hidden;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 6px;
  display: inline-flex;
`;

const Tag = styled.div`
  height: 20px;
  padding-left: 6px;
  padding-right: 6px;
  background: rgba(68, 129, 129, 0.08);
  border-radius: 6px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  display: flex;
  color: var(--Primary-Normal, #448181);
  font-size: 11px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 1;
  letter-spacing: 0.34px;
  word-wrap: break-word;
`;

const ViewAllButton = styled.button`
  padding: 12px 28px;
  border-radius: 12px;
  border: 1px solid var(--Primary-Normal, #448181);
  background: transparent;
  color: var(--Primary-Normal, #448181);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ChevronIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 20px;
    height: 20px;
  }
`;

const ArchiveAvatar = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid var(--Line-Normal-Alternative, rgba(112, 115, 124, 0.08));
`;

const Divider = styled.div`
  width: 1px;
  height: 12px;
  background: var(--Line-Normal-Normal, rgba(112, 115, 124, 0.22));
`;

const CommunityList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const CommunityCard = styled.div`
  width: 100%;
  border-top: 1px solid var(--Line-Normal-Normal, rgba(112, 115, 124, 0.22));
  cursor: pointer;
`;

const CommunityContent = styled.div`
  padding: 32px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const CommunityMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CommunityTitle = styled.div`
  color: var(--Label-Normal, #171719);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
`;

const CommunityBody = styled.div`
  min-height: 44px;
  color: var(--Label-Neutral, rgba(46, 47, 51, 0.88));
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 21.99px;
  letter-spacing: 0.20px;
`;

const CommunityCategories = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  span {
    color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
    font-size: 14px;
    font-family: 'Pretendard JP', sans-serif;
    font-weight: 600;
    line-height: 20.01px;
    letter-spacing: 0.20px;
  }
`;

const CommunityMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CommunityAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  span {
    color: var(--Label-Neutral, rgba(46, 47, 51, 0.88));
    font-size: 14px;
    font-family: 'Pretendard JP', sans-serif;
    font-weight: 600;
    line-height: 20.01px;
    letter-spacing: 0.20px;
    
    &:last-child {
      color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
      font-weight: 400;
    }
  }
`;

const CommunityStats = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  span {
    color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
    font-size: 14px;
    font-family: 'Pretendard JP', sans-serif;
    font-weight: 400;
    line-height: 20.01px;
    letter-spacing: 0.20px;
  }
`;

// 스켈레톤 UI 컴포넌트들
const ClassImageSkeleton = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const ClassTitleSkeleton = styled.div`
  width: 80%;
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const ClassExtraCaptionSkeleton = styled.div`
  width: 60%;
  height: 14px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  margin-top: 4px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const TagSkeleton = styled.div`
  width: 50px;
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 6px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

// 빈 상태 메시지 컴포넌트
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
`;

const EmptyStateIcon = styled.div`
  width: 48px;
  height: 48px;
  margin-bottom: 16px;
  opacity: 0.3;
  
  svg {
    width: 100%;
    height: 100%;
    fill: var(--Label-Neutral, rgba(46, 47, 51, 0.88));
  }
`;

const EmptyStateTitle = styled.h3`
  color: var(--Label-Normal, #171719);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  margin: 0 0 8px 0;
`;

const EmptyStateDescription = styled.p`
  color: var(--Label-Neutral, rgba(46, 47, 51, 0.88));
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20px;
  margin: 0;
`;

// 무한 스크롤용 로딩 컴포넌트들
const LoadingMoreContainer = styled.div`
  width: 100%;
  padding: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

const LoadingSpinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid var(--Line-Normal-Alternative, rgba(112, 115, 124, 0.08));
  border-top: 3px solid var(--Primary-Normal, #448181);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20px;
`;
